import {
	type AnyFrontendStage,
	type FrontendPlacementMessage,
} from '@post-office/message-maker-browser/types';
import type {
	AnyBackendMessageStage,
	BackendPlacementMessage,
	BackendSyncMessageStage,
} from '@post-office/message-maker-node/placements/types';
import { noValidation } from '@post-office/message-maker-node/placements/message-helpers';
import type {
	AnyPayload,
	CreatePlacementMessageConfig,
} from '@post-office/message-template-contracts/placement-v2';
import {
	PlacementBackend,
	PlacementFrontend,
} from '@post-office/placements--growth/confluence-side-nav';
import type { TransformMetadataResult } from '@post-office/shared-contracts/message-template-metadata';
import { ConfluenceCloudCcpId } from '@post-office/message-template-contracts/message-template-metadata';
import { useCallback, useMemo } from 'react';

import { assertMessageTemplateContext } from '../../../__generated__/validation/messageTemplateContext';
import { SideNavBanner } from '../../../ui/SideNavBanner';
import { hydrate, type ConfluenceSideNavHydrateResult } from './hydrate';

export const MessageBackend: (config: CreatePlacementMessageConfig) => BackendPlacementMessage<{
	transformMetadata: BackendSyncMessageStage<AnyPayload, TransformMetadataResult>;
	hydrate?: AnyBackendMessageStage;
}> = PlacementBackend.createMessageBackend(() => ({
	transformMetadata: {
		validate: assertMessageTemplateContext,
		stage: () => ({
			rerankingMetadata: { productId: ConfluenceCloudCcpId },
		}),
	},
	hydrate: {
		validate: assertMessageTemplateContext,
		stage: hydrate,
	},
}));

type MessageFrontendProps = ConfluenceSideNavHydrateResult & {
	handleOnDismissed: () => void;
};

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: AnyFrontendStage;
}> = PlacementFrontend.createMessageFrontend({
	render: {
		validate: noValidation<MessageFrontendProps>(),
		Component: ({
			productName,
			edition,
			entitlementId,
			handleOnDismissed,
		}: MessageFrontendProps) => {
			const analyticsAttributes = useMemo(
				() => ({
					productName,
					edition,
					entitlementId,
				}),
				[productName, edition, entitlementId],
			);

			const handleBannerClick = useCallback(() => {}, []);

			return (
				<SideNavBanner
					onClick={handleBannerClick}
					onDismiss={handleOnDismissed}
					productName={productName}
					edition={edition}
					bannerName="confluenceRecentDowngradeDiscountBanner"
					analyticsAttributes={analyticsAttributes}
				/>
			);
		},
	},
});
